'use client'
import { useLazyQuery, useQuery } from '@apollo/client'
import { useRouter } from 'next/navigation'
import { useEffect, useState } from 'react'

import fancy from '@/assets/images/error-images/fancy.svg'
import { GET_SIMILAR_PLAYERS } from '@/graphql/comparePlayer/comparePlayer.query'
import { GET_SEARCH_PLAYER } from '@/graphql/search/search.query'
import { BLUR_DATA_URL_BASE64 } from '@/shared/constants'
import useDebounce from '@/shared/hooks/useDebounce'
import { getImgURL, parallelCalls } from '@/shared/utils'
import MyImage from '../../myImage'
import NoData from '../../noData'

const getSearchPayload = (search: string | undefined, limit: number) => {
  return {
    nLimit: limit,
    nSkip: 1,
    sSearch: search,
    sSortBy: 'dCreated',
    nOrder: -1
  }
}

export default function ComparePlayerPopup({ playerId, hide, slug, isSecond = false }: any) {
  const [searchVal, setSearchVal] = useState('')
  const router = useRouter()
  const [suggestions, setSuggestions] = useState<any>({})
  const enhancedSearch = useDebounce(searchVal, 500)
  const [searchPlayer, { loading: searchLoading }] = useLazyQuery(GET_SEARCH_PLAYER)

  const { data, loading: isLoading } = useQuery(GET_SIMILAR_PLAYERS, {
    variables: {
      input: {
        iPlayerId: playerId,
        nLimit: 15
      }
    },
    onCompleted: d => {
      setSuggestions(d?.getSimilarPlayers?.filter((p: any) => p !== playerId))
    }
  })

  function onResults(data: any) {
    const [player] = data
    if (player?.data?.data?.getPlayerSearch?.aResults?.length > 0) {
      setSuggestions(player?.getPlayerSearch?.aResults?.filter((p: any) => p?._id !== playerId))
    } else setSuggestions([])
  }

  const changeSearch = async (val: string) => {
    try {
      if (val?.length === 0) {
        setSuggestions(data?.getSimilarPlayers?.filter((p: any) => p?._id !== playerId))
      }

      if (!val || val.length < 3) {
        return
      }

      if (!val.endsWith(' ')) {
        const data = await parallelCalls([
          searchPlayer({ variables: { input: getSearchPayload(val, 4) } })
        ])
        onResults(data)
      }
    } catch (err) {
      console.error('err', err)
    }
  }

  useEffect(() => {
    changeSearch(enhancedSearch)
  }, [enhancedSearch])

  function PlayerCard({ player }: any) {
    return (
      <div
        key={player?._id}
        className="flex items-center gap-2 border border-transparent focus:border-primary-500 rounded-xl p-2 hover:bg-primary-500/5 hover:border-primary-500 cursor-pointer"
        onClick={() => generateSlugUrl(player?.oSeo?.sSlug)}
      >
        <MyImage
          src={player?.oImg?.sUrl ? getImgURL(player?.oImg?.sUrl) : ''}
          blurDataURL={BLUR_DATA_URL_BASE64}
          placeholderType="players"
          width={28}
          height={28}
          className="rounded-full size-9 md:size-12 flex-shrink-0"
        />
        <div className="flex flex-col">
          <span className="text-sm md:text-base hover:text-primary-500">
            {player?.sFullName || '-'}
          </span>
          <span className="text-xs md:text-sm text-t-secondary">{player?.sCountryFull || '-'}</span>
        </div>
      </div>
    )
  }

  function generateSlugUrl(player2slug: string) {
    let p1Slug
    let p2Slug
    if (isSecond) {
      p1Slug = slug.split('/')?.[1]
      p2Slug = player2slug.split('/')?.[1]
    } else {
      console.log('else')
      p1Slug = player2slug.split('/')?.[1]
      p2Slug = slug.split('/')?.[1]
    }
    const finalSlug = `/players/compare/${p1Slug}-vs-${p2Slug}`
    router.push(finalSlug)
    hide()
  }

  return (
    <div className="h-screen w-full fixed top-0 left-0 z-50">
      <div
        className="absolute top-0 left-0 w-full h-full bg-black/30 bg-opacity-30"
        onClick={hide}
      ></div>
      <div className="absolute top-40 left-1/2 -translate-x-1/2 w-[calc(100%_-_20px)] max-w-[700px] bg-card rounded-lg mx-auto">
        <div className="flex flex-col items-center justify-center h-full border-2 border-c rounded-lg">
          <input
            type="text"
            autoFocus
            onChange={e => setSearchVal(e.target.value)}
            className={`w-full p-2 ${enhancedSearch ? 'border-b-2' : ''} dark:bg-[#32393a] bg-white text-t-origin border-c px-5 py-3 rounded-lg`}
            placeholder="Search..."
          />
        </div>
        <div className="mt-4 p-2 rounded-lg">
          <div className="grid md:h-full max-h-[400px] overflow-y-auto grid-cols sm:grid-cols-2 md:grid-cols-3 md:gap-4">
            {(isLoading || searchLoading) && (
              <div className="flex justify-start items-center p-2">
                <span className="text-primary-500">Loading...</span>
              </div>
            )}
            {suggestions?.length > 0 &&
              !isLoading &&
              !searchLoading &&
              suggestions?.map((p: any) => <PlayerCard key={p?._id} player={p} />)}
          </div>
          {suggestions?.length === 0 && !isLoading && !searchLoading && (
            <div className="flex justify-center w-full items-center p-2">
              <NoData imagePath={fancy} />{' '}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
